import { Flex, theme, Tooltip } from 'antd';
import type { SemanaDeEstudo } from '@titularis/api-client';
import { veuDaCor } from '@titularis/ui';
import { descricaoDaSemana, intensidadeDaSemana, type Intensidade } from './recorrencia';

/**
 * As últimas semanas de uma conta, uma célula por semana, a atual à direita.
 *
 * **É a resposta a "essa pessoa volta?" que cabe numa linha de tabela.** O total
 * de dias diz quanto, e não diz quando: cinco dias na semana do cadastro e cinco
 * dias espalhados por cinco semanas são o mesmo número e dois comportamentos
 * opostos - a primeira experimentou com vontade e sumiu, a segunda criou hábito.
 * A faixa separa as duas de relance.
 *
 * **As semanas são do calendário, e não da conta.** A mesma célula é a mesma
 * semana em todas as linhas, então descer os olhos pela coluna mostra quem
 * estudou na semana do último post. A semana de antes da conta existir fica
 * tracejada e vazia: pintada de cinza, ela diria "estava aqui e não estudou", e
 * toda conta nova pareceria abandonada.
 *
 * A cor é o `colorPrimary` com véu, e não uma escala escrita à mão: no escuro o
 * primário já vem clareado pelo tema, e a faixa acompanha sem código a mais.
 */
export function FaixaDeSemanas({
  semanas,
  tamanho = 12,
}: {
  semanas: SemanaDeEstudo[];
  tamanho?: number;
}) {
  const { token } = theme.useToken();

  const fundo: Record<Exclude<Intensidade, 'antes'>, string> = {
    0: token.colorFill,
    1: veuDaCor(token.colorPrimary, 0.4),
    2: veuDaCor(token.colorPrimary, 0.7),
    3: token.colorPrimary,
  };

  const estudadas = semanas.filter((semana) => (semana.dias ?? 0) > 0).length;

  return (
    <Flex
      gap={3}
      role="img"
      aria-label={`Estudou em ${estudadas} das últimas ${semanas.length} semanas`}
    >
      {semanas.map((semana, indice) => {
        const intensidade = intensidadeDaSemana(semana.dias);

        return (
          <Tooltip
            key={semana.inicio}
            title={descricaoDaSemana(semana, indice === semanas.length - 1)}
          >
            <span
              style={{
                display: 'block',
                flex: 'none',
                width: tamanho,
                height: tamanho,
                boxSizing: 'border-box',
                borderRadius: token.borderRadiusXS,
                ...(intensidade === 'antes'
                  ? { border: `1px dashed ${token.colorBorder}` }
                  : { background: fundo[intensidade] }),
              }}
            />
          </Tooltip>
        );
      })}
    </Flex>
  );
}
